import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import './elementStock.css';
import { boolProp, choiceProp, listProp, paragraphsOf, textDirection, textProp, type ElementProps } from './elementProps';
import { FittedLine } from './FittedLine';
import { sanitizeElementHtml } from './htmlSanitize';

// The three ways an element carries words. A heading and a paragraph are both edited through a Content
// box that can bold a word and colour it — which is what LightFunnels does for every one of them — so
// either may arrive holding markup, and both go through the same sanitiser `custom-html` does. Copy that carries no tag is still drawn as escaped text, which is what
// every tree written before the Content box holds.

/** The heading levels a merchant may pick, spelled the way the catalog's select stores them. */
const LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

/** What a rich-text box leaves behind and a plain one never does: the opening of a tag, or a character
 *  reference — the Content box saves "Bottes & co" as `Bottes &amp; co`, with no tag around it. */
const MARKUP_RE = /<[a-zA-Z/]|&(?:#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});/;

// Reports whether stored copy is markup, so words written before the Content box existed stay escaped.
const isMarkup = (value: string): boolean => MARKUP_RE.test(value);

// Resolves the tag a heading draws as, from the `h2` the inspector stores or the bare 2 an older tree kept.
const headingTag = (props: ElementProps): (typeof LEVELS)[number] => {
  const stored = props.level;
  if (typeof stored !== 'number' || !Number.isFinite(stored)) return choiceProp(props, 'level', LEVELS, 'h2');
  return LEVELS[Math.min(LEVELS.length, Math.max(1, Math.round(stored))) - 1] ?? 'h2';
};

// Draws a heading at the level the merchant chose, with the formatting its Content box let them write in.
export const HeadingElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const text = textProp(node.props, 'text');
  const Tag = headingTag(node.props);
  const dir = textDirection(node.props);
  if (!isMarkup(text)) return <Tag {...box} dir={dir}>{text}</Tag>;
  return <Tag {...box} dir={dir} dangerouslySetInnerHTML={{ __html: sanitizeElementHtml(text) }} />;
};

// Reads whether the product a bound stock line speaks for can be ordered, so its dot and colour say so too.
function useStockState(node: Parameters<ElementComponent>[0]['node']): 'in' | 'out' | null {
  const { store } = useElementRender();
  const stock = store?.product?.stock;
  if (node.bindings?.text !== 'product.stock' || stock === null || stock === undefined) return null;
  return stock > 0 ? 'in' : 'out';
}

// Draws written copy: the formatting its Content box allows, or a new paragraph wherever older words left a blank line.
export const ParagraphElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const text = textProp(node.props, 'text');
  const dir = textDirection(node.props);
  // Product badges are ordinary bound paragraphs so merchants can still select and style them. The
  // renderer adds only their semantic skin, which also upgrades already-installed theme trees.
  const productBadge = node.bindings?.text === 'product.badge';
  const stock = useStockState(node);
  const badge = productBadge ? {
    ...box,
    className: [box.className, 'xe-cat-badge'].filter(Boolean).join(' '),
    'data-kind': text === 'New' ? 'new' : 'sale'
  } : stock ? { ...box, className: [box.className, 'xe-stock'].filter(Boolean).join(' '), 'data-state': stock } : box;
  if (isMarkup(text)) return <div {...badge} dir={dir} dangerouslySetInnerHTML={{ __html: sanitizeElementHtml(text) }} />;
  if (boolProp(node.props, 'fitWidth')) return <FittedLine box={badge} dir={dir} text={text} />;
  const parts = paragraphsOf(text);
  if (parts.length < 2) return <p {...badge} dir={dir}>{parts[0] ?? ''}</p>;
  return <div {...badge} dir={dir}>{parts.map((part, index) => <p key={`${index}-${part.slice(0, 12)}`}>{part}</p>)}</div>;
};

/** Every marker the catalog offers, and the `list-style-type` each one is; a tick is the glyph itself. */
const MARKERS = {
  disc: 'disc', circle: 'circle', square: 'square', number: 'decimal',
  'lower-alpha': 'lower-alpha', 'lower-roman': 'lower-roman', tick: '"✓  "', none: 'none'
} as const;

/** The three markers that count, which is what makes a list an `ol` and its lines readable in order. */
const ORDERED: readonly string[] = ['number', 'lower-alpha', 'lower-roman'];

/** Where the marker sits. Neither this nor the type is a style property, so both are written inline. */
const POSITIONS = ['outside', 'inside'] as const;

// Draws a list of lines with the marker the merchant chose, as an `ol` when that marker counts.
export const ListElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const marker = choiceProp(node.props, 'marker', Object.keys(MARKERS) as (keyof typeof MARKERS)[], 'disc');
  const style = {
    listStyleType: MARKERS[marker],
    listStylePosition: choiceProp(node.props, 'markerPosition', POSITIONS, 'outside')
  };
  const rows = listProp(node.props, 'items').map((item, index) => <li key={`${index}-${item.slice(0, 12)}`}>{item}</li>);
  const dir = textDirection(node.props);
  return ORDERED.includes(marker)
    ? <ol {...box} style={style} dir={dir}>{rows}</ol>
    : <ul {...box} style={style} dir={dir}>{rows}</ul>;
};
